import React, { useState, useEffect } from "react";
import { flushSync } from "react-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Loader2, Search } from "lucide-react";
import { useClinics } from "@/hooks/clinics/useClinics";
import { clinicsGraphqlService, Facility } from "@/services/api/clinics/clinicsGraphqlService";
import { miscGraphqlService } from "@/services/api/misc/miscGraphqlService";
import { useAuth } from "@/contexts/AuthContext";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useProgramTypeDashboard } from "@/hooks/workflow/useProgramTypeDashboard";

interface FilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: AppliedFilters) => void;
  initialFilters?: AppliedFilters;
}

export interface AppliedFilters {
  clinic_id?: number[];
  provider_id?: number[];
  clinic_facility_id?: number[];
  primary_insurance_payor?: string[];
  secondary_insurance_payor?: string[];
  condition?: string[];
  zip_code?: string[];
  program_eligibility?: string[];
}

interface EMRProvider {
  staff_id: number;
  staff_name: string;
}

interface PrimaryInsurance {
  insurance_payor: string;
  insurance_id: number;
}

export const FilterModal: React.FC<FilterModalProps> = ({
  open,
  onOpenChange,
  onApply,
  initialFilters
}) => {
  const [selectedClinic, setSelectedClinic] = useState<string>("");
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [providerSearchOpen, setProviderSearchOpen] = useState(false);

  // Track Radix Select open states so we can reliably close portals before the dialog closes.
  // This prevents lingering layers that can block clicks and make the page feel "broken".
  const [facilitiesSelectOpen, setFacilitiesSelectOpen] = useState(false);
  const [programEligibilitySelectOpen, setProgramEligibilitySelectOpen] = useState(false);
  const [programStatusSelectOpen, setProgramStatusSelectOpen] = useState(false);
  const [attemptsSelectOpen, setAttemptsSelectOpen] = useState(false);

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [emrProviders, setEMRProviders] = useState<EMRProvider[]>([]);
  const [isFacilitiesLoading, setIsFacilitiesLoading] = useState(false);
  const [isProvidersLoading, setIsProvidersLoading] = useState(false);
  const [diagnosisList, setDiagnosisList] = useState<Array<{ active: boolean; cause: string }>>([]);
  const [isDiagnosisLoading, setIsDiagnosisLoading] = useState(false);
  const [selectedPrimaryDx, setSelectedPrimaryDx] = useState<string>("");
  const [selectedSecondaryDx, setSelectedSecondaryDx] = useState<string>("");
  const [primaryDxSearchOpen, setPrimaryDxSearchOpen] = useState(false);
  const [secondaryDxSearchOpen, setSecondaryDxSearchOpen] = useState(false);
  const [primaryInsuranceList, setPrimaryInsuranceList] = useState<PrimaryInsurance[]>([]);
  const [isPrimaryInsuranceLoading, setIsPrimaryInsuranceLoading] = useState(false);
  const [selectedPrimaryInsurance, setSelectedPrimaryInsurance] = useState<string>("");
  const [primaryInsuranceSearchOpen, setPrimaryInsuranceSearchOpen] = useState(false);
  
  const [secondaryInsuranceList, setSecondaryInsuranceList] = useState<PrimaryInsurance[]>([]);
  const [isSecondaryInsuranceLoading, setIsSecondaryInsuranceLoading] = useState(false);
  const [selectedSecondaryInsurance, setSelectedSecondaryInsurance] = useState<string>("");
  const [secondaryInsuranceSearchOpen, setSecondaryInsuranceSearchOpen] = useState(false);
  const [selectedProgramEligibility, setSelectedProgramEligibility] = useState<string>("");
  const [zipCode, setZipCode] = useState<string>("");
  const { clinics, loading: isLoading } = useClinics(1000); // Fetch all clinics
  const { hasuraToken } = useAuth();
  const { programTypes, loading: isProgramTypesLoading } = useProgramTypeDashboard();

  const unlockPointerEventsIfSafe = React.useCallback(() => {
    // Radix sets `body { pointer-events: none }` when a modal layer is active.
    // If a portal/layer cleanup is interrupted, it can get stuck even after close.
    // We only "unlock" when we detect *no* active open dialog/listbox layer.
    try {
      const hasOpenDialog = !!document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
      );
      const hasOpenListbox = !!document.querySelector('[role="listbox"][data-state="open"]');

      if (hasOpenDialog || hasOpenListbox) return;

      const candidates: Array<HTMLElement | null> = [
        document.body,
        document.documentElement,
        document.getElementById("root"),
        document.getElementById("app"),
      ];

      for (const el of candidates) {
        if (!el) continue;
        if (el.style.pointerEvents === "none") {
          el.style.pointerEvents = "";
        }
      }
    } catch {
      // no-op
    }
  }, []);

  const scheduleUnlockPointerEvents = React.useCallback(() => {
    // Run multiple times because the "stuck pointer-events" mutation can happen
    // after unmount/cleanup (seen in session replays).
    const run = () => unlockPointerEventsIfSafe();

    run();
    requestAnimationFrame(run);
    requestAnimationFrame(() => requestAnimationFrame(run));
    window.setTimeout(run, 50);
    window.setTimeout(run, 250);
  }, [unlockPointerEventsIfSafe]);

  const closeAllPopovers = React.useCallback(() => {
    setClinicSearchOpen(false);
    setProviderSearchOpen(false);
    setPrimaryDxSearchOpen(false);
    setSecondaryDxSearchOpen(false);
    setPrimaryInsuranceSearchOpen(false);
    setSecondaryInsuranceSearchOpen(false);

    setFacilitiesSelectOpen(false);
    setProgramEligibilitySelectOpen(false);
    setProgramStatusSelectOpen(false);
    setAttemptsSelectOpen(false);
  }, []);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Flush so portals close before Dialog cleanup runs.
      flushSync(() => closeAllPopovers());
      onOpenChange(false);
      scheduleUnlockPointerEvents();
      return;
    }
    onOpenChange(true);
  };

  // Safety net: even if close is triggered outside our explicit handlers,
  // make sure the document doesn't remain non-interactive.
  useEffect(() => {
    if (!open) scheduleUnlockPointerEvents();
  }, [open, scheduleUnlockPointerEvents]);

  // Initialize filters from initialFilters when modal opens
  useEffect(() => {
    if (open && initialFilters) {
      if (initialFilters.clinic_id?.[0]) {
        setSelectedClinic(initialFilters.clinic_id[0].toString());
      }
      if (initialFilters.clinic_facility_id?.length) {
        setSelectedFacilities(initialFilters.clinic_facility_id.map(f => f.toString()));
      }
      if (initialFilters.provider_id?.[0]) {
        setSelectedProvider(initialFilters.provider_id[0].toString());
      }
      if (initialFilters.primary_insurance_payor?.[0]) {
        setSelectedPrimaryInsurance(initialFilters.primary_insurance_payor[0]);
      }
      if (initialFilters.secondary_insurance_payor?.[0]) {
        setSelectedSecondaryInsurance(initialFilters.secondary_insurance_payor[0]);
      }
      if (initialFilters.condition?.length) {
        if (initialFilters.condition[0]) setSelectedPrimaryDx(initialFilters.condition[0]);
        if (initialFilters.condition[1]) setSelectedSecondaryDx(initialFilters.condition[1]);
      }
      if (initialFilters.zip_code?.length) {
        setZipCode(initialFilters.zip_code.join(', '));
      }
      if (initialFilters.program_eligibility?.[0]) {
        setSelectedProgramEligibility(initialFilters.program_eligibility[0]);
      }
    }
  }, [open, initialFilters]);

  // Fetch diagnosis list when popup opens
  useEffect(() => {
    const fetchDiagnosisList = async () => {
      if (!open || !hasuraToken) return;

      setIsDiagnosisLoading(true);
      try {
        const data = await miscGraphqlService.getPrescriptionCauseList(hasuraToken);
        setDiagnosisList(data);
      } catch (error) {
        console.error("Error fetching diagnosis list:", error);
        setDiagnosisList([]);
      } finally {
        setIsDiagnosisLoading(false);
      }
    };

    fetchDiagnosisList();
  }, [open, hasuraToken]);

  // Fetch insurance lists when popup opens
  useEffect(() => {
    const fetchInsuranceLists = async () => {
      if (!open || !hasuraToken) return;

      setIsPrimaryInsuranceLoading(true);
      setIsSecondaryInsuranceLoading(true);
      
      try {
        const [primaryData, secondaryData] = await Promise.all([
          clinicsGraphqlService.getPrimaryInsuranceList(hasuraToken),
          clinicsGraphqlService.getSecondaryInsuranceList(hasuraToken)
        ]);
        
        setPrimaryInsuranceList(primaryData);
        setSecondaryInsuranceList(secondaryData);
      } catch (error) {
        console.error("Error fetching insurance lists:", error);
        setPrimaryInsuranceList([]);
        setSecondaryInsuranceList([]);
      } finally {
        setIsPrimaryInsuranceLoading(false);
        setIsSecondaryInsuranceLoading(false);
      }
    };

    fetchInsuranceLists();
  }, [open, hasuraToken]);

  // Fetch facilities when clinic is selected
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!selectedClinic || !hasuraToken) {
        setFacilities([]);
        return;
      }

      setIsFacilitiesLoading(true);
      
      try {
        const facilitiesData = await clinicsGraphqlService.getFacilitiesByClinic(
          parseInt(selectedClinic),
          hasuraToken
        );
        
        setFacilities(facilitiesData);
      } catch (error) {
        console.error("Error fetching facilities:", error);
        setFacilities([]);
      } finally {
        setIsFacilitiesLoading(false);
      }
    };

    fetchFacilities();
  }, [selectedClinic, hasuraToken]);

  // Fetch providers when clinic is selected
  useEffect(() => {
    const fetchProviders = async () => {
      if (!selectedClinic || !hasuraToken) {
        setEMRProviders([]);
        setSelectedProvider("");
        return;
      }

      setIsProvidersLoading(true);
      
      try {
        const providersData = await clinicsGraphqlService.getProvidersByClinicFromEMR(
          parseInt(selectedClinic),
          hasuraToken
        );
        
        setEMRProviders(providersData);
      } catch (error) {
        console.error("Error fetching providers:", error);
        setEMRProviders([]);
      } finally {
        setIsProvidersLoading(false);
      }
    };

    fetchProviders();
  }, [selectedClinic, hasuraToken]);

  // Filter secondary dx to exclude selected primary dx
  const availableSecondaryDx = diagnosisList.filter(
    dx => dx.cause !== selectedPrimaryDx
  );

  const handleApply = () => {
    const filters: AppliedFilters = {};
    
    if (selectedClinic) {
      filters.clinic_id = [parseInt(selectedClinic)];
    }
    
    if (selectedProvider) {
      filters.provider_id = [parseInt(selectedProvider)];
    }
    
    if (selectedFacilities.length > 0) {
      filters.clinic_facility_id = selectedFacilities.map(f => parseInt(f));
    }
    
    if (selectedPrimaryInsurance) {
      filters.primary_insurance_payor = [selectedPrimaryInsurance];
    }
    
    if (selectedSecondaryInsurance) {
      filters.secondary_insurance_payor = [selectedSecondaryInsurance];
    }
    
    // Add condition (diagnosis) filter
    const conditions: string[] = [];
    if (selectedPrimaryDx) conditions.push(selectedPrimaryDx);
    if (selectedSecondaryDx) conditions.push(selectedSecondaryDx);
    if (conditions.length > 0) {
      filters.condition = conditions;
    }
    
    // Add zip code filter
    if (zipCode.trim()) {
      filters.zip_code = zipCode.split(',').map(z => z.trim()).filter(Boolean);
    }
    
    // Add program eligibility filter
    if (selectedProgramEligibility) {
      filters.program_eligibility = [selectedProgramEligibility];
    }
    
    // Important: close any nested popovers/select portals before closing the dialog.
    // Use flushSync to ensure Radix portals are torn down before the Dialog unmounts.
    flushSync(() => closeAllPopovers());
    onApply(filters);
    onOpenChange(false);
    scheduleUnlockPointerEvents();
  };

  const handleClear = () => {
    setSelectedClinic("");
    setSelectedFacilities([]);
    setSelectedProvider("");
    setSelectedPrimaryDx("");
    setSelectedSecondaryDx("");
    setSelectedPrimaryInsurance("");
    setSelectedSecondaryInsurance("");
    setSelectedProgramEligibility("");
    setZipCode("");
  };

  const handleCancel = () => {
    flushSync(() => closeAllPopovers());
    onOpenChange(false);
    scheduleUnlockPointerEvents();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Select Clinics and Providers</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Clinics and Providers Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              {isLoading ? (
                <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Loading clinics...</span>
                </div>
              ) : (
                <Popover modal={true} open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clinicSearchOpen}
                      className="w-full justify-between"
                    >
                      {selectedClinic
                        ? clinics.find((clinic) => clinic.clinic_id.toString() === selectedClinic)?.clinic_name
                        : "Select Clinic"}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                    <Command>
                      <CommandInput placeholder="Search clinic..." />
                      <CommandList className="max-h-[200px] overflow-y-auto">
                        <CommandEmpty>No clinic found.</CommandEmpty>
                        <CommandGroup>
                          {clinics.map((clinic) => (
                            <CommandItem
                              key={clinic.clinic_id}
                              value={clinic.clinic_name}
                              onSelect={() => {
                                setSelectedClinic(clinic.clinic_id.toString());
                                setClinicSearchOpen(false);
                              }}
                            >
                              {clinic.clinic_name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            
            <div className="space-y-2">
              {isFacilitiesLoading ? (
                <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Loading facilities...</span>
                </div>
              ) : (
                <Select 
                  open={facilitiesSelectOpen}
                  onOpenChange={setFacilitiesSelectOpen}
                  disabled={!selectedClinic}
                  value={selectedFacilities[0] || ""}
                  onValueChange={(value) => setSelectedFacilities([value])}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Facility Name" />
                  </SelectTrigger>
                  <SelectContent className="z-[60] bg-popover">
                    {facilities.length > 0 ? (
                      facilities.map((facility) => (
                        <SelectItem key={facility.facility_id} value={facility.facility_id.toString()}>
                          {facility.facility_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-data" disabled>
                        No facilities available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="space-y-2">
              {isProvidersLoading ? (
                <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                  <span className="text-sm text-muted-foreground">Loading providers...</span>
                </div>
              ) : (
                <Popover modal={true} open={providerSearchOpen} onOpenChange={setProviderSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={providerSearchOpen}
                      className="w-full justify-between"
                      disabled={!selectedClinic}
                    >
                      {selectedProvider
                        ? emrProviders.find((p) => p.staff_id.toString() === selectedProvider)?.staff_name
                        : "Select Provider"}
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                    <Command>
                      <CommandInput placeholder="Search provider..." />
                      <CommandList className="max-h-[200px] overflow-y-auto">
                        <CommandEmpty>No provider found.</CommandEmpty>
                        <CommandGroup>
                          {emrProviders.map((provider) => (
                            <CommandItem
                              key={provider.staff_id}
                              value={provider.staff_name}
                              onSelect={() => {
                                setSelectedProvider(provider.staff_id.toString());
                                setProviderSearchOpen(false);
                              }}
                            >
                              <span className="text-sm">{provider.staff_name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* Filters Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Filters</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                {isDiagnosisLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading diagnoses...</span>
                  </div>
                ) : (
                  <Popover modal={true} open={primaryDxSearchOpen} onOpenChange={setPrimaryDxSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={primaryDxSearchOpen}
                        className="w-full justify-between"
                      >
                        {selectedPrimaryDx || "Select Primary Dx"}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                      <Command>
                        <CommandInput placeholder="Search primary diagnosis..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No diagnosis found.</CommandEmpty>
                          <CommandGroup>
                            {diagnosisList.map((dx, index) => (
                              <CommandItem
                                key={`primary-${dx.cause}-${index}`}
                                value={dx.cause}
                                onSelect={(value) => {
                                  setSelectedPrimaryDx(value);
                                  setPrimaryDxSearchOpen(false);
                                }}
                              >
                                {dx.cause}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              
              <div className="space-y-2">
                {isDiagnosisLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading diagnoses...</span>
                  </div>
                ) : (
                  <Popover modal={true} open={secondaryDxSearchOpen} onOpenChange={setSecondaryDxSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={secondaryDxSearchOpen}
                        className="w-full justify-between"
                      >
                        {selectedSecondaryDx || "Select Secondary Dx"}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                      <Command>
                        <CommandInput placeholder="Search secondary diagnosis..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No diagnosis found.</CommandEmpty>
                          <CommandGroup>
                            {availableSecondaryDx.map((dx, index) => (
                              <CommandItem
                                key={`secondary-${dx.cause}-${index}`}
                                value={dx.cause}
                                onSelect={(value) => {
                                  setSelectedSecondaryDx(value);
                                  setSecondaryDxSearchOpen(false);
                                }}
                              >
                                {dx.cause}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                {isPrimaryInsuranceLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading insurance...</span>
                  </div>
                ) : (
                  <Popover modal={true} open={primaryInsuranceSearchOpen} onOpenChange={setPrimaryInsuranceSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={primaryInsuranceSearchOpen}
                        className="w-full justify-between"
                      >
                        {selectedPrimaryInsurance || "Select Primary Insurance"}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                      <Command>
                        <CommandInput placeholder="Search primary insurance..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No insurance found.</CommandEmpty>
                          <CommandGroup>
                            {primaryInsuranceList.map((insurance) => (
                              <CommandItem
                                key={insurance.insurance_id}
                                value={insurance.insurance_payor}
                                onSelect={(value) => {
                                  setSelectedPrimaryInsurance(value);
                                  setPrimaryInsuranceSearchOpen(false);
                                }}
                              >
                                <div className="flex flex-col w-full">
                                  <span className="text-sm">{insurance.insurance_payor}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ID: {insurance.insurance_id}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              
              <div className="space-y-2">
                {isSecondaryInsuranceLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading insurance...</span>
                  </div>
                ) : (
                  <Popover modal={true} open={secondaryInsuranceSearchOpen} onOpenChange={setSecondaryInsuranceSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={secondaryInsuranceSearchOpen}
                        className="w-full justify-between"
                      >
                        {selectedSecondaryInsurance || "Select Secondary Insurance"}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0 z-[60] bg-popover">
                      <Command>
                        <CommandInput placeholder="Search secondary insurance..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No insurance found.</CommandEmpty>
                          <CommandGroup>
                            {secondaryInsuranceList.map((insurance) => (
                              <CommandItem
                                key={insurance.insurance_id}
                                value={insurance.insurance_payor}
                                onSelect={(value) => {
                                  setSelectedSecondaryInsurance(value);
                                  setSecondaryInsuranceSearchOpen(false);
                                }}
                              >
                                <div className="flex flex-col w-full">
                                  <span className="text-sm">{insurance.insurance_payor}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ID: {insurance.insurance_id}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              
              <div className="space-y-2">
                {isProgramTypesLoading ? (
                  <div className="flex items-center justify-center h-10 border rounded-md bg-background">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                    <span className="text-sm text-muted-foreground">Loading program types...</span>
                  </div>
                ) : (
                  <Select
                    open={programEligibilitySelectOpen}
                    onOpenChange={setProgramEligibilitySelectOpen}
                    value={selectedProgramEligibility}
                    onValueChange={setSelectedProgramEligibility}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Program Eligibility" />
                    </SelectTrigger>
                    <SelectContent className="z-[60] bg-popover">
                      {programTypes.length > 0 ? (
                        programTypes.map((programType) => (
                          <SelectItem key={programType} value={programType}>
                            {programType}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-data" disabled>
                          No program types available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            
            
            <div className="space-y-2">
              <Input 
                placeholder="Enter Zip Code Separately by Commas" 
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
              />
            </div>
          </div>

          {/* Date Range Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Select Last Visited Date Range</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Input type="date" placeholder="From: DD/MM" />
              </div>
              <div className="space-y-2">
                <Input type="date" placeholder="To: DD/MM" />
              </div>
            </div>
          </div>

          {/* Additional Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select open={programStatusSelectOpen} onOpenChange={setProgramStatusSelectOpen}>
              <SelectTrigger>
                <SelectValue placeholder="Program Status" />
              </SelectTrigger>
               <SelectContent className="z-[60] bg-popover">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            
            <Select open={attemptsSelectOpen} onOpenChange={setAttemptsSelectOpen}>
              <SelectTrigger>
                <SelectValue placeholder="No. of Attempts" />
              </SelectTrigger>
               <SelectContent className="z-[60] bg-popover">
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
                <SelectItem value="4+">4+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClear} className="text-destructive hover:text-destructive">
            Clear
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleApply} className="bg-primary hover:bg-primary/90">
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};